import { useEffect } from 'react';

interface SEOData {
  title?: string;
  description?: string;
  keywords?: string[];
  ogImage?: string;
  ogImageAlt?: string;
  canonicalUrl?: string;
  type?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  siteName?: string;
  locale?: string;
  /** Defaults to index, follow */
  robots?: string;
}

export const useSEO = (data: SEOData) => {
  useEffect(() => {
    document.documentElement.lang = 'es';

    if (data.title) {
      document.title = data.title;
    }

    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updatePropertyTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    if (data.description) {
      updateMetaTag('description', data.description);
    }

    if (data.keywords && data.keywords.length > 0) {
      updateMetaTag('keywords', data.keywords.join(', '));
    }

    if (data.title) {
      updatePropertyTag('og:title', data.title);
    }

    if (data.description) {
      updatePropertyTag('og:description', data.description);
    }

    if (data.ogImage) {
      updatePropertyTag('og:image', data.ogImage);
      updatePropertyTag('og:image:width', '1200');
      updatePropertyTag('og:image:height', '630');
      updatePropertyTag('og:image:type', 'image/jpeg');
      if (data.ogImage.startsWith('https://')) {
        updatePropertyTag('og:image:secure_url', data.ogImage);
      }
    }

    if (data.ogImageAlt) {
      updatePropertyTag('og:image:alt', data.ogImageAlt);
    }

    updatePropertyTag('og:type', data.type || 'article');

    if (data.canonicalUrl) {
      const fullUrl = data.canonicalUrl.startsWith('http')
        ? data.canonicalUrl
        : `${window.location.origin}${data.canonicalUrl}`;
      updatePropertyTag('og:url', fullUrl);
    }

    updatePropertyTag('og:site_name', data.siteName || 'Statemia');
    updatePropertyTag('og:locale', data.locale || 'es_ES');

    if (data.type === 'article') {
      if (data.author) {
        updatePropertyTag('article:author', data.author);
      }
      if (data.publishedTime) {
        updatePropertyTag('article:published_time', data.publishedTime);
      }
      if (data.modifiedTime) {
        updatePropertyTag('article:modified_time', data.modifiedTime);
      }
      if (data.section) {
        updatePropertyTag('article:section', data.section);
      }
      if (data.tags && data.tags.length > 0) {
        data.tags.forEach((tag) => {
          updatePropertyTag('article:tag', tag);
        });
      }
    }

    if (data.title) {
      updateMetaTag('twitter:title', data.title);
    }

    if (data.description) {
      updateMetaTag('twitter:description', data.description);
    }

    if (data.ogImage) {
      updateMetaTag('twitter:image', data.ogImage);
      updateMetaTag('twitter:image:width', '1200');
      updateMetaTag('twitter:image:height', '630');
    }

    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:site', '@statemia_app');
    if (data.author) {
      updateMetaTag('twitter:creator', data.author);
    }

    if (data.canonicalUrl) {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      const fullUrl = data.canonicalUrl.startsWith('http')
        ? data.canonicalUrl
        : `${window.location.origin}${data.canonicalUrl}`;
      canonical.href = fullUrl;
    }

    if (data.author) {
      updateMetaTag('linkedin:owner', data.author);
    }

    updateMetaTag('pinterest-rich-pin', 'true');
    if (data.ogImage) {
      updateMetaTag('pinterest-image', data.ogImage);
    }

    updateMetaTag('whatsapp:description', data.description || '');
    updateMetaTag('telegram:channel', '@statemia_app');
    updateMetaTag('discord:embed', 'true');

    const robots = data.robots || 'index, follow';
    updateMetaTag('robots', robots);
    updateMetaTag('googlebot', robots);

    updateMetaTag('language', 'Spanish');
    updateMetaTag('geo.region', 'ES');
    updateMetaTag('geo.country', 'Spain');

    updateMetaTag('mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent');

    updateMetaTag('theme-color', '#1a1a1a');
    updateMetaTag('msapplication-TileColor', '#1a1a1a');
  }, [data]);
};

/** Convenience: noindex private / auth pages */
export function useNoIndexPage(title: string, description?: string) {
  useSEO({
    title,
    description: description || 'Área privada de Statemia.',
    type: 'website',
    robots: 'noindex, nofollow',
  });
}
